<template>
  <button
    @click="toggleFavourite"
    class="p-2 hover:bg-gray-100 rounded transition-colors"
    :title="isFavourited ? 'Unfavourite' : 'Favourite'"
  >
    <font-awesome-icon
      icon="star"
      :class="isFavourited ? 'text-yellow-500' : 'text-gray-300 hover:text-gray-400'"
      class="text-lg transition-colors"
    />
  </button>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { db } from '../../db.js';
import { resolveRouteTitle } from './routeMetaTitle.js';
import {
  buildSettingsEntry,
  hasSettingsIdentity,
  normalizeSettingsList,
  sameSettingsEntry,
  sanitizeSettingsList,
} from './userdataSettings.js';

const store = useStore();
const route = useRoute();

const props = defineProps({
  pageItem: Object,
  contextData: Object
});

// Get current favourites list, normalized to array
const favourites = computed(() => normalizeSettingsList(store.state.user?.Settings?.favourites));

// Check if current item is favourited
const isFavourited = computed(() => {
  const currentPath = route.path;
  const currentCollection = route.meta?.dataPath;
  const currentId = props.pageItem?.ID || route.params.id;
  
  return favourites.value.some(fav => sameSettingsEntry(fav, {
    collection: currentCollection,
    id: currentId,
    path: currentPath
  }));
});

// Toggle favourite state
async function toggleFavourite() {
  const currentPath = route.path;
  const currentCollection = route.meta?.dataPath || route.meta?.ParentType || route.meta?.Collection || route.meta?.App;
  const currentId = props.pageItem?.ID || route.params.id;
  const meta = route.meta;

  // Skip when the route has no path or id to reopen.
  if (!currentPath || !currentId) {
    return;
  }
  
  let updatedFavourites = [...favourites.value];
  const identity = {
    collection: currentCollection,
    id: currentId,
    path: currentPath
  };
  
  // Find existing favourite by collection+id or path
  const existingIndex = updatedFavourites.findIndex(fav => sameSettingsEntry(fav, identity));
  
  if (existingIndex !== -1) {
    // Remove from favourites
    updatedFavourites.splice(existingIndex, 1);
  } else {
    // Add to favourites
    const title = resolveRouteTitle(meta, props.pageItem, props.contextData);
    
    const newFavourite = buildSettingsEntry({
      path: currentPath,
      collection: currentCollection,
      id: currentId,
      title: title,
      type: meta.name,
      section: meta.app,
      time: new Date().toISOString()
    });

    if (!hasSettingsIdentity(newFavourite)) return;
    
    updatedFavourites.push(newFavourite);
  }
  
  updatedFavourites = sanitizeSettingsList(updatedFavourites);

  // Persist to Firebase. Skip when the userdata key is missing.
  const uid = store.getters.userID;
  if (uid) {
    await db.set(`Userdata/${uid}/Settings/favourites`, updatedFavourites);
  }
}
</script>
